/**
 * Central native runtime-permission helpers for CAMERA and LOCATION.
 *
 * Two ways in:
 *   • requestInitialPermissions() — fired once shortly after login (alongside
 *     push registration in native-lifecycle) so a new user is asked for
 *     camera + location up front, rather than being surprised mid-task.
 *   • ensureCameraPermission() / ensureLocationPermission() — called at the
 *     exact point of use (face scan, transport "Start Trip") so the feature
 *     ALWAYS prompts when the permission is missing and returns a clear
 *     result the UI can act on (proceed, or guide the user to Settings).
 *
 * Notifications are handled separately by registerNativePush() (native-push.ts).
 *
 * Camera note: this app has no @capacitor/camera plugin — every camera surface
 * uses the WebView `getUserMedia` API. So camera permission is primed/checked
 * by opening then immediately releasing a video stream, which triggers the OS
 * prompt on first use. The AndroidManifest declares CAMERA and Info.plist
 * declares NSCameraUsageDescription, so the WebView is allowed to ask.
 */
import { isNative } from "./platform";
import { BgTracker } from "./native-bg-tracker";
import { getNativePlugin } from "./native-plugin";

export type PermissionResult = "granted" | "denied" | "prompt" | "unavailable";

/**
 * Ensure camera access, prompting on first use. Returns "granted" when a video
 * stream could be opened, "denied" when the user (or OS policy) refused, and
 * "unavailable" when there's no camera / the API is missing.
 *
 * Opening a stream here is cheap and idempotent: once granted the OS returns
 * it instantly with no prompt, and we release the tracks immediately so the
 * calling component can open its own stream cleanly.
 */
export async function ensureCameraPermission(): Promise<PermissionResult> {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    return "unavailable";
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    stream.getTracks().forEach((t) => t.stop());
    return "granted";
  } catch (err) {
    const name = err instanceof DOMException ? err.name : "";
    // No physical camera / cannot be started → not a permission problem.
    if (name === "NotFoundError" || name === "OverconstrainedError" || name === "NotReadableError") {
      return "unavailable";
    }
    // NotAllowedError / SecurityError / anything else → treat as denied.
    return "denied";
  }
}

/**
 * Ensure location access, prompting when the OS is in the "prompt" state.
 * On native this is a pre-flight check the driver flow runs BEFORE
 * committing a trip start (`POST /transport/trips/start`) — a denial here
 * must abort before the server has a trip with no tracker behind it, not
 * after. Routed through the BgTracker plugin (not `@capacitor/geolocation`)
 * so this reports the SAME permission state the actual tracking session
 * (driver-tracker.ts) will use — asking a different plugin about the same
 * OS permission is how a pre-flight check and the real tracker disagree.
 * On web we can only report the queryable state — the actual prompt fires
 * when a position is requested.
 */
export async function ensureLocationPermission(): Promise<PermissionResult> {
  if (isNative()) {
    try {
      let status = await BgTracker.checkPermissions();
      if (status.location !== "granted") {
        status = await BgTracker.requestForegroundPermission();
      }
      return status.location === "granted"
        ? "granted"
        : status.location === "denied"
          ? "denied"
          : "prompt";
    } catch {
      return "unavailable";
    }
  }
  if (typeof navigator !== "undefined" && navigator.permissions) {
    try {
      const res = await navigator.permissions.query({ name: "geolocation" as PermissionName });
      return res.state === "granted" ? "granted" : res.state === "denied" ? "denied" : "prompt";
    } catch {
      return "prompt";
    }
  }
  return "prompt";
}

let initialRequested = false;

/**
 * One-time, best-effort upfront request for camera, fired shortly after the
 * first authenticated load. A denial here is not fatal — the point-of-use
 * guard (ensureCameraPermission) re-prompts or guides the user later.
 *
 * LOCATION IS DELIBERATELY NOT REQUESTED HERE — only at the exact point of
 * use: attendance geofence check-ins (WHILE-IN-USE, one-shot) and the
 * driver "Start Trip" flow (foreground THEN, best-effort, background — see
 * native-bg-tracker.ts / driver-tracker.ts; 2026-09-22). Requesting
 * location app-wide right after login has no purpose context attached and
 * is what got the app flagged by Play review, so it was removed. The
 * driver flow still shows an in-app disclosure before the OS prompt (see
 * hasLocationDisclosureAccepted / location-disclosure-dialog.tsx) — and,
 * since the driver flow now collects location in the background, that
 * disclosure is no longer optional UX polish but a Play-policy requirement
 * (see location-disclosure-dialog.tsx's own comment).
 */
export async function requestInitialPermissions(): Promise<void> {
  if (!isNative() || initialRequested) return;
  initialRequested = true;
  try {
    await ensureCameraPermission();
  } catch {
    /* non-fatal */
  }
}

// Persisted flag: has the user seen and accepted the location prominent
// disclosure at least once? Google requires the disclosure before the first
// location request; once accepted we don't re-gate every trip. Bump the
// version suffix if the disclosure wording materially changes.
//
// v1 → v2 (2026-09-22): the disclosure now truthfully describes background
// collection during a trip (foreground service / Always authorization —
// see native-bg-tracker.ts) instead of the old "never in the background"
// claim. A driver who accepted v1's promise did not consent to what the
// app does now, so the bump re-shows the dialog once per install.
const LOCATION_DISCLOSURE_KEY = "campushub-location-disclosure-v2";

export function hasLocationDisclosureAccepted(): boolean {
  try {
    return localStorage.getItem(LOCATION_DISCLOSURE_KEY) === "accepted";
  } catch {
    return false;
  }
}

export function markLocationDisclosureAccepted(): void {
  try {
    localStorage.setItem(LOCATION_DISCLOSURE_KEY, "accepted");
  } catch {
    /* storage unavailable — the disclosure will just show again next time */
  }
}

/**
 * Open this app's own page in the OS Settings app (Settings → Apps →
 * CampusHub on Android, Settings → CampusHub on iOS) — the only route
 * back once camera/location has been denied permanently.
 *
 * Root-cause sweep for face check-in (2026-09-26): camera is requested via
 * the WebView's `getUserMedia`, never `@capacitor/camera`, so there is no
 * plugin anywhere that can re-show the OS permission prompt after a user
 * denies it (Android: "Don't ask again"; iOS: denying once is permanent
 * until Settings). The self-scan screen had copy telling the user to go to
 * Settings but nothing that actually took them there — indistinguishable,
 * to that user, from "the camera doesn't work on my phone".
 *
 * Backed by `AppSettingsPlugin` (native-overrides/{android,ios}), which
 * needs a native rebuild to exist on a device — see that plugin's own
 * comment. Returns false (never throws) when the plugin isn't present yet
 * (older installed build, or web) so a caller can fall back to the
 * instructional copy it already shows.
 */
export async function openNativeAppSettings(): Promise<boolean> {
  if (!isNative()) return false;
  try {
    const plugin = getNativePlugin<{ open: () => Promise<void> }>("AppSettingsPlugin");
    if (!plugin?.open) return false;
    await plugin.open();
    return true;
  } catch {
    return false;
  }
}
